import { ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, matchPath, useLocation } from "react-router-dom";

import type { MenuGroup, MenuItem } from "../../../navigation/menuConfig";
import { getModuleSidebarIconTone } from "../../../navigation/menuConfig";

import {
  SIDEBAR_HOVER_BG,
  SIDEBAR_ITEM_ACTIVE,
  SIDEBAR_ITEM_BASE,
  SIDEBAR_ITEM_COLLAPSED,
  SIDEBAR_ITEM_EXPANDED,
  SIDEBAR_MUTED_TEXT,
  SIDEBAR_TEXT_INACTIVE,
} from "./sidebarClassNames";

import { Button } from "@/components/Button";

import "./sidebarTree.css";

const MenuItemIcon = memo(function MenuItemIcon({
  Icon,
  toneClassName,
}: {
  Icon: MenuItem["icon"];
  toneClassName: string;
}) {
  return <Icon className={clsx("w-5 h-5", toneClassName)} aria-hidden="true" />;
});

export function SidebarGroup({
  group,
  collapsed,
  onItemClick,
  showGroupLabel,
}: {
  group: MenuGroup;
  collapsed: boolean;
  onItemClick?: () => void;
  showGroupLabel?: boolean;
}) {
  const location = useLocation();
  const pathname = location.pathname;
  const tree = !collapsed && group.collapsible !== false;
  const itemsWithChildren = useMemo(
    () =>
      group.items
        .filter((item) => (item.children?.length ?? 0) > 0)
        .map((item) => item.id),
    [group.items],
  );
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  // Prefer a specific sibling destination over its shared URL prefix.
  const activeHref = useMemo(
    () =>
      group.items
        .flatMap((item) => [
          item.href,
          ...(item.children ?? []).map((child) => child.href),
        ])
        .filter((href) => matchPath({ path: href, end: false }, pathname))
        .sort((left, right) => right.length - left.length)[0],
    [group.items, pathname],
  );
  const isItemActive = useCallback(
    (href: string): boolean => href === activeHref,
    [activeHref],
  );

  useEffect(() => {
    setExpandedItems((current) => {
      const next: Record<string, boolean> = {};

      for (const itemId of itemsWithChildren) {
        if (current[itemId]) {
          next[itemId] = true;
        }
      }

      for (const item of group.items) {
        const hasActiveChild = item.children?.some((child) => isItemActive(child.href));
        if (hasActiveChild) {
          next[item.id] = true;
        }
      }

      return next;
    });
  }, [group.items, isItemActive, itemsWithChildren]);

  function toggleItem(itemId: string) {
    setExpandedItems((current) => ({
      ...current,
      [itemId]: !current[itemId],
    }));
  }

  return (
    <div className={clsx(collapsed ? "px-2" : "px-3")}>
      {!collapsed && showGroupLabel && (
        <div
          className={clsx(
            "text-[11px] uppercase tracking-wider px-2 py-2",
            SIDEBAR_MUTED_TEXT,
          )}
        >
          {group.name}
        </div>
      )}

      <ul className={tree ? "sidebar-tree" : "space-y-1"}>
        {group.items.map((item) => {
          const isActive = isItemActive(item.href);
          const childItems = item.children ?? [];
          const hasChildren = !collapsed && childItems.length > 0;
          const hasActiveChild = childItems.some((child) => isItemActive(child.href));
          const isBranchActive = isActive || hasActiveChild;
          const isExpanded = hasChildren && (expandedItems[item.id] ?? hasActiveChild);

          return (
            <li key={item.id} className={tree ? "sidebar-tree-item" : "space-y-1"}>
              <div className={tree ? "sidebar-tree-row" : "flex items-stretch gap-1"}>
                <NavLink
                  to={item.href}
                  end={!isActive}
                  onClick={onItemClick}
                  className={clsx(
                    SIDEBAR_ITEM_BASE,
                    tree
                      ? "sidebar-tree-link"
                      : collapsed
                        ? SIDEBAR_ITEM_COLLAPSED
                        : SIDEBAR_ITEM_EXPANDED,
                    hasChildren ? "flex-1 pr-3" : "w-full",
                    isBranchActive
                      ? tree
                        ? "sidebar-tree-link--active"
                        : SIDEBAR_ITEM_ACTIVE
                      : !tree && [SIDEBAR_TEXT_INACTIVE, SIDEBAR_HOVER_BG],
                  )}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={collapsed ? item.name : undefined}
                  title={item.name}
                >
                  {!tree && (
                    <span
                      aria-hidden="true"
                      className={clsx(
                        "absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r",
                        isBranchActive
                          ? "bg-blue-600 dark:bg-blue-400"
                          : "bg-transparent",
                      )}
                    />
                  )}

                  <span
                    className={clsx(
                      "flex items-center",
                      tree ? "sidebar-tree-icon" : collapsed ? "pl-0" : "pl-2",
                    )}
                  >
                    <MenuItemIcon
                      Icon={item.icon}
                      toneClassName={getModuleSidebarIconTone(
                        item.toneKey ?? group.id,
                        isBranchActive,
                      )}
                    />
                  </span>

                  {!collapsed && (
                    <span
                      className={clsx(
                        "min-w-0 flex-1 truncate",
                        !tree && "text-sm font-medium",
                      )}
                    >
                      {item.name}
                    </span>
                  )}
                </NavLink>

                {hasChildren ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => toggleItem(item.id)}
                    className={clsx(
                      "sidebar-tree-toggle !p-0 text-slate-500 dark:text-slate-400",
                      isBranchActive && "text-blue-600 dark:text-blue-300",
                    )}
                    aria-expanded={isExpanded}
                    aria-label={`${item.name} submenu`}
                  >
                    <ChevronDownIcon
                      className={clsx(
                        "h-4 w-4 transition-transform",
                        isExpanded ? "rotate-180" : "rotate-0",
                      )}
                    />
                  </Button>
                ) : null}
              </div>

              {hasChildren && isExpanded ? (
                <ul className="sidebar-tree sidebar-tree--nested">
                  {childItems.map((child) => {
                    const isChildActive = isItemActive(child.href);

                    return (
                      <li key={child.id} className="sidebar-tree-item">
                        <NavLink
                          to={child.href}
                          end={!isChildActive}
                          onClick={onItemClick}
                          className={clsx(
                            "sidebar-tree-link",
                            isChildActive && "sidebar-tree-link--active",
                          )}
                          aria-current={isChildActive ? "page" : undefined}
                          title={child.name}
                        >
                          <span className="sidebar-tree-icon">
                            <MenuItemIcon
                              Icon={child.icon}
                              toneClassName={getModuleSidebarIconTone(
                                child.toneKey ?? group.id,
                                isChildActive,
                              )}
                            />
                          </span>
                          <span className="min-w-0 flex-1 truncate">{child.name}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
